import { Download } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { cn } from '@/utils/cn';

/**
 * A QR code at a chosen scale, with a download.
 *
 * Used for giving links, the check-in kiosk display and TOTP enrolment. Rendered
 * locally rather than through an image service, because a giving link should not be
 * handed to a third party to draw, and the kiosk has to keep working when its
 * connection does not.
 *
 * Error correction is set high so a code still scans when it is printed on a bulletin
 * and photographed from three rows back.
 *
 * Downloads can carry the church logo, name and campus so a printed poster is
 * recognisable without the console chrome around it.
 */

export interface QrBranding {
  churchName: string;
  /** Campus / branch when the church uses one — shown under the church name. */
  campusName?: string | null;
  /** Public logo URL; omitted from the download if it cannot be drawn onto the canvas. */
  logoUrl?: string | null;
}

export interface QrPanelProps {
  /** The URL or payload encoded in the code. */
  value: string;
  /** Pixel size of the rendered code. The kiosk uses a far larger one than a form. */
  size?: number;
  /** Text under the code — the link itself, or an instruction. */
  caption?: string;
  downloadLabel?: string;
  /** Filename stem for the download; the extension is added. */
  downloadName?: string;
  /** Accessible description of where the code leads. */
  label: string;
  /** Church logo, name and campus drawn onto the downloaded PNG. */
  branding?: QrBranding;
  className?: string;
}

const DOWNLOAD_QR_SIZE = 512;
const DOWNLOAD_LOGO_SIZE = 96;

const wrapCanvasText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines;
};

const loadImageFromDataUrl = (dataUrl: string): Promise<HTMLImageElement | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(blob);
  });

/**
 * Load the logo as an untainted image for canvas export.
 * Spaces public URLs usually lack CORS, so prefer the same-origin API stream.
 */
const loadLogo = async (src: string): Promise<HTMLImageElement | null> => {
  try {
    const blob = await api.getBlob('/churches/current/logo');
    const dataUrl = await blobToDataUrl(blob);
    const fromApi = await loadImageFromDataUrl(dataUrl);
    if (fromApi) return fromApi;
  } catch {
    // Fall through to the public URL.
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
};

const drawContainedImage = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  boxSize: number,
) => {
  const scale = Math.min(boxSize / image.naturalWidth, boxSize / image.naturalHeight);
  const width = image.naturalWidth * scale;
  const height = image.naturalHeight * scale;
  const dx = x + (boxSize - width) / 2;
  const dy = y + (boxSize - height) / 2;
  ctx.drawImage(image, dx, dy, width, height);
};

const buildBrandedDownload = async (
  value: string,
  branding: QrBranding,
): Promise<HTMLCanvasElement> => {
  const qrCanvas = document.createElement('canvas');
  await QRCode.toCanvas(qrCanvas, value, {
    errorCorrectionLevel: 'H',
    margin: 2,
    width: DOWNLOAD_QR_SIZE,
    color: { dark: '#000000', light: '#ffffff' },
  });

  const churchName = branding.churchName.trim();
  const campusName = branding.campusName?.trim() || '';
  const showCampus =
    campusName.length > 0 &&
    campusName.toLocaleLowerCase() !== churchName.toLocaleLowerCase();

  const logoUrl = branding.logoUrl?.trim() || '';
  const logo = logoUrl ? await loadLogo(logoUrl) : null;

  const paddingX = 48;
  const paddingTop = 40;
  const paddingBottom = 48;
  const gapAfterLogo = 20;
  const gapAfterTitle = 12;
  const gapBeforeQr = 28;
  const contentWidth = DOWNLOAD_QR_SIZE;
  const canvasWidth = contentWidth + paddingX * 2;
  const textMaxWidth = contentWidth;

  // Measure title block on a throwaway context so we know the final height.
  const measure = document.createElement('canvas').getContext('2d');
  if (!measure) return qrCanvas;

  measure.font = '600 36px system-ui, -apple-system, Segoe UI, sans-serif';
  const nameLines = wrapCanvasText(measure, churchName, textMaxWidth);
  measure.font = '400 26px system-ui, -apple-system, Segoe UI, sans-serif';
  const campusLines = showCampus
    ? wrapCanvasText(measure, campusName, textMaxWidth)
    : [];

  const nameLineHeight = 44;
  const campusLineHeight = 34;
  const logoBlockHeight = logo ? DOWNLOAD_LOGO_SIZE + gapAfterLogo : 0;
  const titleHeight =
    nameLines.length * nameLineHeight +
    (campusLines.length > 0 ? gapAfterTitle + campusLines.length * campusLineHeight : 0);

  const canvasHeight =
    paddingTop +
    logoBlockHeight +
    titleHeight +
    gapBeforeQr +
    DOWNLOAD_QR_SIZE +
    paddingBottom;

  const out = document.createElement('canvas');
  out.width = canvasWidth;
  out.height = canvasHeight;
  const ctx = out.getContext('2d');
  if (!ctx) return qrCanvas;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  let y = paddingTop;

  if (logo) {
    drawContainedImage(
      ctx,
      logo,
      (canvasWidth - DOWNLOAD_LOGO_SIZE) / 2,
      y,
      DOWNLOAD_LOGO_SIZE,
    );
    y += DOWNLOAD_LOGO_SIZE + gapAfterLogo;
  }

  ctx.fillStyle = '#111827';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.font = '600 36px system-ui, -apple-system, Segoe UI, sans-serif';
  for (const line of nameLines) {
    ctx.fillText(line, canvasWidth / 2, y);
    y += nameLineHeight;
  }

  if (campusLines.length > 0) {
    y += gapAfterTitle;
    ctx.fillStyle = '#4b5563';
    ctx.font = '400 26px system-ui, -apple-system, Segoe UI, sans-serif';
    for (const line of campusLines) {
      ctx.fillText(line, canvasWidth / 2, y);
      y += campusLineHeight;
    }
  }

  y += gapBeforeQr;
  ctx.drawImage(qrCanvas, paddingX, y, DOWNLOAD_QR_SIZE, DOWNLOAD_QR_SIZE);

  return out;
};

export const QrPanel = ({
  value,
  size = 200,
  caption,
  downloadLabel,
  downloadName = 'qr-code',
  label,
  branding,
  className,
}: QrPanelProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setReady(false);
    void QRCode.toCanvas(canvas, value, {
      errorCorrectionLevel: 'H',
      margin: 2,
      width: size,
      // Fixed black on white rather than themed: a QR code in brand colours on a dark
      // surface is a QR code that half the phones in the room will not read.
      color: { dark: '#000000', light: '#ffffff' },
    }).then(() => setReady(true));
  }, [size, value]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const trigger = (dataUrl: string) => {
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${downloadName}.png`;
      link.click();
    };

    if (!branding?.churchName.trim()) {
      trigger(canvas.toDataURL('image/png'));
      return;
    }

    void buildBrandedDownload(value, branding)
      .then((branded) => {
        try {
          trigger(branded.toDataURL('image/png'));
        } catch {
          // A cross-origin logo can taint the canvas; fall back to the bare QR.
          trigger(canvas.toDataURL('image/png'));
        }
      })
      .catch(() => {
        trigger(canvas.toDataURL('image/png'));
      });
  };

  const campus = branding?.campusName?.trim();
  const churchName = branding?.churchName.trim();
  const logoUrl = branding?.logoUrl?.trim();
  const showCampus =
    Boolean(campus) &&
    Boolean(churchName) &&
    campus!.toLocaleLowerCase() !== churchName!.toLocaleLowerCase();

  return (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      {churchName || logoUrl ? (
        <div className="flex max-w-xs flex-col items-center gap-2 text-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt=""
              className="size-12 rounded-lg object-cover"
            />
          ) : null}
          {churchName ? (
            <div>
              <p className="text-body-sm font-semibold text-fg">{churchName}</p>
              {showCampus ? (
                <p className="text-caption text-fg-secondary">{campus}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        className="relative rounded-lg bg-white p-3 shadow-e1"
        style={{ width: size + 24, height: size + 24 }}
      >
        {ready ? null : (
          <Skeleton className="absolute inset-3" style={{ width: size, height: size }} />
        )}
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={label}
          className={cn('block', !ready && 'invisible')}
        />
      </div>

      {caption ? (
        <p className="max-w-xs text-center text-caption break-all text-fg-secondary">
          {caption}
        </p>
      ) : null}

      {downloadLabel ? (
        <Button
          variant="outline"
          size="sm"
          onClick={download}
          disabled={!ready}
          leadingIcon={<Download className="h-4 w-4" />}
        >
          {downloadLabel}
        </Button>
      ) : null}
    </div>
  );
};
